import { TFunction } from 'i18next'
import { z } from 'zod'

import { DAY_NAMES_NARROW, DayNameNarrow } from '@/constants'
import { MARKET_SHOP_MAX_TIP_AMOUNT, MARKET_SHOP_MIN_TIP_AMOUNT } from '@/models'

const makeMarketShopCategorySchema = (t: TFunction<['market-shop-dashboard']>) =>
  z.object({
    // Note: If new category is added on local state - it has id equal to `new-category-${Date.now()}`
    id: z.number().or(z.string()),
    name: z.string().max(20, t('market-shop-dashboard:settings.categories.validation.maxLength')),
    position: z.number(),
  })

// Note: Form uses a different structure than API for better UX (includes an enabled flag)
export interface MarketShopBusinessHoursFormModel {
  name: DayNameNarrow
  enabled: boolean
  startTime: number | null
  endTime: number | null
}

const MarketShopBusinessHoursFormSchema = z.object({
  enabled: z.boolean(),
  endTime: z.number().nullable(),
  name: z.enum(DAY_NAMES_NARROW),
  startTime: z.number().nullable(),
})

export const makeMarketShopSettingsSchema = (t: TFunction<['market-shop-dashboard']>) =>
  z
    .object({
      businessHours: z.array(MarketShopBusinessHoursFormSchema),
      categories: z.array(makeMarketShopCategorySchema(t)),
      deliverToMe: z.boolean(),
      displayNameForCashPayment: z.string(),
      enablePayByCard: z.boolean(),
      enablePayByCash: z.boolean(),
      globalTax: z.string(),
      tips: z.array(z.string()),
      tipsEnabled: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.enablePayByCash) {
        if (data.displayNameForCashPayment.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'market-shop-dashboard:settings.displayNameForCashPayment.validation.required',
            ),
            path: ['displayNameForCashPayment'],
          })
        } else if (data.displayNameForCashPayment.length > 23) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'market-shop-dashboard:settings.displayNameForCashPayment.validation.maxLength',
            ),
            path: ['displayNameForCashPayment'],
          })
        }
      }

      const globalTaxNum = Number(data.globalTax)
      if (data.globalTax !== '' && !isNaN(globalTaxNum)) {
        if (globalTaxNum < 0) {
          ctx.addIssue({
            code: 'custom',
            message: t('market-shop-dashboard:settings.globalTax.validation.min'),
            path: ['globalTax'],
          })
        } else if (globalTaxNum > 100) {
          ctx.addIssue({
            code: 'custom',
            message: t('market-shop-dashboard:settings.globalTax.validation.max'),
            path: ['globalTax'],
          })
        }
      }

      if (!data.tipsEnabled) {
        return
      }

      const seenTips = new Set<number>()

      data.tips.forEach((tip, index) => {
        if (tip.length === 0) {
          ctx.addIssue({
            code: 'invalid_value',
            message: t('market-shop-dashboard:settings.tips.validation.required'),
            path: ['tips', index],
            values: [tip],
          })
        } else if (Number(tip) < MARKET_SHOP_MIN_TIP_AMOUNT) {
          ctx.addIssue({
            code: 'too_small',
            message: t('market-shop-dashboard:settings.tips.validation.minAmount', {
              min: MARKET_SHOP_MIN_TIP_AMOUNT,
            }),
            minimum: MARKET_SHOP_MIN_TIP_AMOUNT,
            origin: 'number',
            path: ['tips', index],
          })
        } else if (Number(tip) > MARKET_SHOP_MAX_TIP_AMOUNT) {
          ctx.addIssue({
            code: 'too_big',
            maximum: MARKET_SHOP_MAX_TIP_AMOUNT,
            message: t('market-shop-dashboard:settings.tips.validation.maxAmount', {
              max: MARKET_SHOP_MAX_TIP_AMOUNT,
            }),
            origin: 'number',
            path: ['tips', index],
          })
        }

        if (seenTips.has(Number(tip))) {
          ctx.addIssue({
            code: 'custom',
            message: t('market-shop-dashboard:settings.tips.validation.duplicate'),
            path: ['tips', index],
          })
        } else {
          seenTips.add(Number(tip))
        }
      })
    })

export type MarketShopSettingsFormData = z.infer<ReturnType<typeof makeMarketShopSettingsSchema>>
